import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import CBSRowCognaraCell from '@/components/evm/CBSRowCognaraCell';
import BulkCbsCognaraPanel from '@/components/evm/BulkCbsCognaraPanel';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, ArrowLeft, RefreshCw, Loader2, BarChart3, AlertTriangle,
  CheckCircle2, Target, Activity, Gauge, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import ModuleInsightsPanel from '@/components/common/ModuleInsightsPanel';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart, Bar, RadialBarChart, RadialBar
} from 'recharts';
import { AgGridReact } from 'ag-grid-react';

function formatCr(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function MetricCard({ title, value, subtitle, color, isIndex = false }) {
  const displayVal = isIndex ? value : formatCr(value);
  const textColor = isIndex ? (value >= 1 ? '#22C55E' : value >= 0.9 ? '#F59E0B' : '#EF4444') : color;
  return (
    <div className="rounded-xl p-4" data-testid={`metric-${title.toLowerCase().replace(/[\s/]+/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="text-xs mb-2 font-medium" style={{ color: 'var(--text-muted)' }}>{title}</div>
      <div className="text-2xl font-bold" style={{ color: textColor }}>{displayVal}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

function IndexGauge({ label, value, target = 1.0, color }) {
  const pct = Math.min(Math.max((value / (target * 1.5)) * 100, 0), 100);
  const gaugeColor = value >= 1 ? '#22C55E' : value >= 0.9 ? '#F59E0B' : '#EF4444';
  return (
    <div className="text-center" data-testid={`gauge-${label.toLowerCase()}`}>
      <div className="relative inline-flex items-center justify-center w-28 h-28">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="40" fill="none" stroke="var(--border-color)" strokeWidth="8" />
          <circle cx="50" cy="50" r="40" fill="none" stroke={gaugeColor} strokeWidth="8"
            strokeDasharray={`${pct * 2.51} ${251 - pct * 2.51}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold" style={{ color: gaugeColor }}>{value.toFixed(2)}</span>
          <span className="text-[9px]" style={{ color: 'var(--text-disabled)' }}>Target: {target.toFixed(1)}</span>
        </div>
      </div>
      <div className="text-xs font-medium mt-1" style={{ color: 'var(--text-primary)' }}>{label}</div>
    </div>
  );
}

export default function EarnedValueManagement() {
  const { projectId: ctxProjectId } = useProject();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [evmData, setEvmData] = useState(null);
  const [sCurve, setSCurve] = useState([]);
  const [trend, setTrend] = useState([]);
  const [cbsRows, setCbsRows] = useState([]);
  const [cbsBusy, setCbsBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpand = useCallback((code) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  const syncFromCbs = useCallback(async () => {
    if (!selectedProject) return;
    setCbsBusy(true);
    try {
      const r = await api.post(`/v1/projects/${selectedProject}/cc/evm/calculate-from-cbs`, {
        periodId: new Date().toISOString().slice(0, 7),
        cutoffDate: new Date().toISOString().slice(0, 10),
      });
      setCbsRows(r.data.elementEVM || []);
      // The calculate endpoint already returns projectEVM — use it as the
      // current snapshot so the KPI ribbon / gauges update immediately.
      setEvmData({ projectEVM: r.data.projectEVM, elementEVM: r.data.elementEVM });
      toast.success(`EVM computed: ${r.data.elementEVM?.length || 0} CBS rows`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CBS EVM sync failed. Run CBS Sync first.');
    } finally { setCbsBusy(false); }
  }, [selectedProject]);

  useEffect(() => {
    api.get('/v1/cc/projects-with-data').then(r => {
      const list = r.data.projects || [];
      setProjects(list);
      // SSOT: honour the currently-selected project from the global context so
      // the EVM page NEVER shows a different project's KPIs than the rest of
      // the dashboard. Falls back to the first project only when nothing is
      // selected globally.
      const activePid = ctxProjectId || (list[0]?.project_id);
      if (activePid) setSelectedProject(activePid);
    }).catch(() => toast.error('Failed to load projects'));
  }, [ctxProjectId]);

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const [evmRes, scRes, trRes] = await Promise.all([
        api.get(`/v1/projects/${selectedProject}/cc/evm/current`).catch(() => null),
        api.get(`/v1/projects/${selectedProject}/cc/evm/s-curve`).catch(() => ({ data: { sCurve: [] } })),
        api.get(`/v1/projects/${selectedProject}/cc/evm/trend`).catch(() => ({ data: { trend: [] } })),
      ]);
      if (evmRes?.data) {
        setEvmData(evmRes.data);
        setCbsRows(evmRes.data.elementEVM || []);
      } else {
        // Auto-calculate on first load so the page never lands empty.
        try {
          const r = await api.post(`/v1/projects/${selectedProject}/cc/evm/calculate-from-cbs`, {
            periodId: new Date().toISOString().slice(0, 7),
            cutoffDate: new Date().toISOString().slice(0, 10),
          });
          setEvmData({ projectEVM: r.data.projectEVM, elementEVM: r.data.elementEVM });
          setCbsRows(r.data.elementEVM || []);
        } catch {
          setEvmData(null);
        }
      }
      setSCurve(scRes?.data?.sCurve || []);
      setTrend(trRes?.data?.trend || []);
    } catch {
      toast.error('Failed to load EVM data');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const evm = evmData?.projectEVM || {};

  const sCurveData = useMemo(() =>
    sCurve.map(s => ({
      period: s.periodId,
      BCWS: Math.round(s.bcws),
      BCWP: Math.round(s.bcwp),
      ACWP: Math.round(s.acwp),
      BAC: Math.round(s.bac),
    }))
  , [sCurve]);

  const trendData = useMemo(() =>
    trend.map(t => ({ period: t.periodId, SPI: t.spi, CPI: t.cpi, CSI: t.csi }))
  , [trend]);

  // Build a hierarchical tree from cbsRows and derive the visible flat list
  // with expand/collapse chevrons — same UX as the CBS Matrix.
  const { evmTreeRows, hasChildrenMap } = useMemo(() => {
    if (!cbsRows?.length) return { evmTreeRows: [], hasChildrenMap: {} };
    const byCode = {};
    cbsRows.forEach(r => { byCode[r.costCode] = { ...r, _children: [] }; });
    const roots = [];
    Object.values(byCode).forEach(n => {
      const p = n.parentCostCode && byCode[n.parentCostCode];
      if (p) p._children.push(n);
      else roots.push(n);
    });
    // Natural sort per depth
    const _sk = (c) => String(c || '').split('.').map(s => { const n = Number(s); return Number.isFinite(n) ? [0, n] : [1, s]; });
    const _cmp = (a, b) => {
      const A = _sk(a), B = _sk(b);
      for (let i = 0; i < Math.max(A.length, B.length); i++) {
        const x = A[i] ?? [0, -Infinity]; const y = B[i] ?? [0, -Infinity];
        if (x[0] !== y[0]) return x[0] - y[0];
        if (x[1] !== y[1]) return x[1] < y[1] ? -1 : 1;
      }
      return 0;
    };
    const sortNode = (n) => { n._children.sort((a, b) => _cmp(a.costCode, b.costCode)); n._children.forEach(sortNode); };
    roots.sort((a, b) => _cmp(a.costCode, b.costCode));
    roots.forEach(sortNode);

    const flat = [];
    const walk = (n) => { flat.push(n); n._children.forEach(walk); };
    roots.forEach(walk);
    const hc = {};
    flat.forEach(n => { if (n._children.length) hc[n.costCode] = true; });
    const visible = flat.filter(n => {
      let p = n.parentCostCode;
      while (p) {
        if (!expanded.has(p)) return false;
        p = byCode[p]?.parentCostCode;
      }
      return true;
    }).map(n => ({ ...n, _hasChildren: !!hc[n.costCode], _expanded: expanded.has(n.costCode) }));
    return { evmTreeRows: visible, hasChildrenMap: hc };
  }, [cbsRows, expanded]);

  // Seed root-level expansion when new data lands
  useEffect(() => {
    if (!cbsRows?.length) return;
    const roots = new Set(cbsRows.filter(r => !r.parentCostCode).map(r => r.costCode));
    setExpanded(prev => (prev.size === 0 ? roots : prev));
  }, [cbsRows]);

  const CostCodeCell = useCallback((p) => {
    const d = p.data || {};
    const lvl = d.level || 0;
    const hasKids = !!d._hasChildren;
    const isOpen = !!d._expanded;
    const Chev = isOpen ? ChevronDown : ChevronRight;
    return (
      <div className="flex items-center h-full" style={{ paddingLeft: `${lvl * 14}px`, gap: 4 }}>
        {hasKids ? (
          <button type="button" data-evm-chevron="1"
            onClick={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); toggleExpand(d.costCode); }}
            className="p-0.5 rounded hover:bg-black/10 flex items-center justify-center"
            style={{ width: 14, height: 14 }}
            data-testid={`evm-toggle-${d.costCode}`}>
            <Chev className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
          </button>
        ) : <span style={{ width: 14, display: 'inline-block' }} />}
        <span style={{ fontWeight: hasKids ? 700 : 400, fontFamily: 'monospace', fontSize: '11px' }}>{d.costCode}</span>
      </div>
    );
  }, [toggleExpand]);

  const costCodeDefs = useMemo(() => [
    { field: 'costCode', headerName: 'Code', width: 170, pinned: 'left', cellRenderer: CostCodeCell, sortable: false, cellStyle: p => ({ padding: 0 }) },
    { field: 'costCodeName', headerName: 'Description', width: 220, flex: 1,
      cellStyle: p => ({ fontWeight: p.data?._hasChildren ? 700 : 400 }) },
    { field: 'costCategory', headerName: 'Category', width: 130 },
    { field: 'bac', headerName: 'BAC', width: 110, type: 'numericColumn', valueFormatter: p => formatCr(p.value) },
    { field: 'bcws', headerName: 'BCWS', width: 110, type: 'numericColumn', valueFormatter: p => formatCr(p.value) },
    { field: 'bcwp', headerName: 'BCWP', width: 110, type: 'numericColumn', valueFormatter: p => formatCr(p.value) },
    { field: 'acwp', headerName: 'ACWP', width: 110, type: 'numericColumn', valueFormatter: p => formatCr(p.value) },
    { field: 'sv', headerName: 'SV', width: 100, type: 'numericColumn', valueFormatter: p => formatCr(p.value), cellStyle: p => ({ color: p.value >= 0 ? '#22C55E' : '#EF4444' }) },
    { field: 'cv', headerName: 'CV', width: 100, type: 'numericColumn', valueFormatter: p => formatCr(p.value), cellStyle: p => ({ color: p.value >= 0 ? '#22C55E' : '#EF4444' }) },
    { field: 'spi', headerName: 'SPI', width: 75, type: 'numericColumn', valueFormatter: p => (p.value || 0).toFixed(2), cellStyle: p => ({ color: p.value >= 1 ? '#22C55E' : p.value >= 0.9 ? '#F59E0B' : '#EF4444', fontWeight: 600 }) },
    { field: 'cpi', headerName: 'CPI', width: 75, type: 'numericColumn', valueFormatter: p => (p.value || 0).toFixed(2), cellStyle: p => ({ color: p.value >= 1 ? '#22C55E' : p.value >= 0.9 ? '#F59E0B' : '#EF4444', fontWeight: 600 }) },
    { field: 'eac', headerName: 'EAC', width: 110, type: 'numericColumn', valueFormatter: p => formatCr(p.value) },
    { field: 'vac', headerName: 'VAC', width: 100, type: 'numericColumn', valueFormatter: p => formatCr(p.value), cellStyle: p => ({ color: p.value >= 0 ? '#22C55E' : '#EF4444' }) },
    { field: 'physicalProgressPct', headerName: '% Complete', width: 95, type: 'numericColumn', valueFormatter: p => `${(p.value || 0).toFixed(1)}%` },
    {
      headerName: 'AI',
      width: 60,
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRenderer: CBSRowCognaraCell,
      cellRendererParams: { projectId: selectedProject },
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
    },
  ], [CostCodeCell, selectedProject]);

  const onEvmRowClicked = useCallback((e) => {
    const t = e.event?.target;
    if (t && t.closest && t.closest('[data-evm-chevron="1"]')) return;
    if (e.data?._hasChildren) toggleExpand(e.data.costCode);
  }, [toggleExpand]);

  const defaultColDef = useMemo(() => ({
    sortable: true, filter: true, resizable: true,
    cellStyle: { fontSize: '12px', display: 'flex', alignItems: 'center' },
  }), []);

  return (
    <DashboardLayout>
      {selectedProject && <AIAnalystPanel projectId={selectedProject} moduleKey="EVM" defaultOpen={true} compact={true} />}

      <div className="space-y-6" data-testid="evm-page">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="p-2.5 rounded-xl" style={{ background: '#22C55E12', border: '1px solid #22C55E20' }}>
            <TrendingUp className="w-5 h-5" style={{ color: '#22C55E' }} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Earned Value Management</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Performance measurement and forecast analytics</p>
          </div>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm" data-testid="evm-project-selector"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button variant="default" size="sm" onClick={syncFromCbs} disabled={cbsBusy} data-testid="sync-from-cbs-btn"
            className="bg-purple-600 hover:bg-purple-700 text-white">
            {cbsBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Gauge className="w-3.5 h-3.5 mr-1" />}
            Sync EVM from CBS
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-text)' }} /></div>
        ) : !evmData ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <AlertTriangle className="w-12 h-12 mb-4" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>No EVM snapshot available for this project</p>
            <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>EVM data is auto-generated during seeding</p>
          </div>
        ) : (
          <>
            {/* Performance Indices */}
            <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="performance-indices">
              <h3 className="text-sm font-semibold mb-5" style={{ color: 'var(--text-primary)' }}>Performance Indices</h3>
              <div className="flex items-center justify-center gap-10 flex-wrap">
                <IndexGauge label="SPI" value={evm.spi || 0} />
                <IndexGauge label="CPI" value={evm.cpi || 0} />
                <IndexGauge label="CSI" value={evm.csi || 0} />
                <IndexGauge label="TCPI" value={evm.tcpi || 0} />
              </div>
            </div>

            {/* EVM Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="evm-metrics">
              <MetricCard title="BAC" value={evm.bac} color="#3B82F6" subtitle="Budget at Completion" />
              <MetricCard title="BCWS (PV)" value={evm.bcws} color="#6366F1" subtitle="Planned Value" />
              <MetricCard title="BCWP (EV)" value={evm.bcwp} color="#22C55E" subtitle="Earned Value" />
              <MetricCard title="ACWP (AC)" value={evm.acwp} color="#EF4444" subtitle="Actual Cost" />
              <MetricCard title="EAC" value={evm.eacSelected || evm.eacCPI || evm.eac} color="#F97316" subtitle="Estimate at Completion" />
              <MetricCard title="VAC" value={evm.vac} color={evm.vac >= 0 ? '#22C55E' : '#EF4444'} subtitle="Variance at Completion" />
            </div>

            {/* Variance Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard title="Schedule Variance (SV)" value={evm.sv} color={evm.sv >= 0 ? '#22C55E' : '#EF4444'} subtitle={`SV% = ${evm.svPct || 0}%`} />
              <MetricCard title="Cost Variance (CV)" value={evm.cv} color={evm.cv >= 0 ? '#22C55E' : '#EF4444'} subtitle={`CV% = ${evm.cvPct || 0}%`} />
              <MetricCard title="Physical Progress" value={`${evm.physicalProgressPct || 0}%`} isIndex color="#0EA5E9" subtitle={`Planned: ${evm.plannedProgressPct || 0}%`} />
              <MetricCard title="ETC" value={evm.etc} color="#143A6B" subtitle="Estimate to Complete" />
            </div>

            {/* EVM S-Curve */}
            <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="evm-scurve">
              <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>EVM S-Curve (BCWS / BCWP / ACWP)</h3>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={sCurveData} margin={{ top: 10, right: 30, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="period" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => formatCr(v)} />
                  <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} formatter={(v) => formatCr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="BCWS" name="BCWS (Planned)" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 3, fill: '#3B82F6' }} />
                  <Line type="monotone" dataKey="BCWP" name="BCWP (Earned)" stroke="#22C55E" strokeWidth={2.5} dot={{ r: 3, fill: '#22C55E' }} />
                  <Line type="monotone" dataKey="ACWP" name="ACWP (Actual)" stroke="#EF4444" strokeWidth={2.5} dot={{ r: 3, fill: '#EF4444' }} />
                  <Line type="monotone" dataKey="BAC" name="BAC" stroke="#94A3B8" strokeWidth={1.5} dot={false} strokeDasharray="8 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* SPI/CPI Trend */}
            {trendData.length > 0 && (
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="spi-cpi-trend">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>SPI / CPI Trend</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={trendData} margin={{ top: 10, right: 30, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="period" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis domain={[0.5, 1.5]} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="SPI" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="CPI" stroke="#22C55E" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="CSI" stroke="#143A6B" strokeWidth={2} dot={{ r: 2 }} strokeDasharray="4 2" />
                    {/* Reference line at 1.0 */}
                    <Line type="monotone" dataKey={() => 1} stroke="#F59E0B" strokeWidth={1} strokeDasharray="6 3" dot={false} name="Target (1.0)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Portfolio-level Cognara sweep across all critical CBS rows */}
            {cbsRows.length > 0 && (
              <BulkCbsCognaraPanel projectId={selectedProject} rows={cbsRows} />
            )}

            {/* Cost Code EVM Breakdown (hierarchical tree) */}
            {evmTreeRows.length > 0 && (
              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="cost-code-grid-container">
                <div className="p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    CBS EVM Breakdown ({cbsRows.length} rows · click a summary row to expand)
                  </h3>
                </div>
                <div className="ag-theme-alpine" style={{ height: 500, width: '100%', '--ag-background-color': 'var(--card-bg)', '--ag-header-background-color': 'var(--table-header-bg)', '--ag-odd-row-background-color': 'var(--table-row-alt)', '--ag-row-hover-color': 'var(--table-row-hover)', '--ag-header-foreground-color': 'var(--text-primary)', '--ag-foreground-color': 'var(--text-primary)', '--ag-border-color': 'var(--border-color)' }}>
                  <AgGridReact rowData={evmTreeRows} columnDefs={costCodeDefs} defaultColDef={defaultColDef}
                    animateRows suppressCellFocus
                    onRowClicked={onEvmRowClicked}
                    getRowStyle={p => p.data?._hasChildren ? { backgroundColor: 'rgba(99,102,241,0.05)', fontWeight: 600 } : {}}
                    getRowId={p => p.data.costCode} rowHeight={30} headerHeight={32} />
                </div>
              </div>
            )}

            {/* Phase EVM Summary */}
            {evmData?.phaseEVM?.length > 0 && (
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="phase-evm">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Phase EVM Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {evmData.phaseEVM.map(phase => (
                    <div key={phase.phase} className="rounded-lg p-4" style={{ border: '1px solid var(--border-color)', backgroundColor: 'var(--app-bg)' }}>
                      <div className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{phase.phase.replace(/_/g, ' ')}</div>
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div><span style={{ color: 'var(--text-muted)' }}>BAC:</span> <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{formatCr(phase.bac)}</span></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>EAC:</span> <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{formatCr(phase.eac)}</span></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>SPI:</span> <span className="font-bold" style={{ color: phase.spi >= 1 ? '#22C55E' : '#EF4444' }}>{phase.spi}</span></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>CPI:</span> <span className="font-bold" style={{ color: phase.cpi >= 1 ? '#22C55E' : '#EF4444' }}>{phase.cpi}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {selectedProject && (
          <ModuleInsightsPanel projectId={selectedProject} moduleKey="evm" color="#8B5CF6" deepLinkBase="/project-controls/cost/evm" />
        )}
      </div>
    </DashboardLayout>
  );
}
